import {
  FormControl,
  FormControlProps,
  InputBaseComponentProps,
  TextField,
  TextFieldProps,
  useThemeProps,
} from '@mui/material';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { forwardRef, useId } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  type ValidationRule,
} from 'react-hook-form';
import { PatternFormat, PatternFormatProps } from 'react-number-format';
import { PHONE_FIELD_PLACEHOLDER, US_PHONE_CODE } from 'src/components/Forms/const';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { removeCodeFromPhoneNumber } from 'src/components/Forms/utils';

export type FormPhoneInputProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'textFieldProps'
> & {
  label: string;
  helperText?: string;
  required?: boolean | string;
  minLength?: number;
  maxLength?: number;
  pattern?: ValidationRule<RegExp>;
  textFieldProps?: Partial<TextFieldProps>;
  value?: string;
  debounced?: number;
} & CommonFormFieldProps<'phone', string>;

interface RenderPhoneInputProps {
  field: ControllerRenderProps<FieldValues, string>;
  textFieldProps?: Partial<TextFieldProps>;
  variant: TextFieldProps['variant'];
  size?: TextFieldProps['size'];
  label?: string;
  helperText?: string;
  error: boolean;
  value?: string;
  debounced?: number;
}

export const TextMaskPhone = forwardRef<
  PatternFormatProps,
  Omit<InputBaseComponentProps, 'onChange'> & {
    onChange: (event: { target: { value: string } }) => void;
  }
>(function NumericFormatCustom(props, ref) {
  const { onChange, ...rest } = props;

  return (
    <PatternFormat
      {...rest}
      getInputRef={ref}
      onPaste={(event) => {
        const digits = event.clipboardData.getData('text').replace(/\D/g, '');
        const phoneNumber = removeCodeFromPhoneNumber(digits);
        if (phoneNumber === digits) return;
        event.preventDefault();
        (onChange as (param: { target: unknown }) => void)?.({
          target: { value: phoneNumber },
        });
      }}
      onValueChange={(values) => {
        (onChange as (param: { target: unknown }) => void)?.({
          target: { value: values.value },
        });
      }}
      format={`${US_PHONE_CODE} (###) ### ####`}
    />
  );
});

const RenderPhoneInput = ({
  field,
  textFieldProps,
  label,
  error,
  variant,
  size,
  value,
  helperText,
}: RenderPhoneInputProps) => {
  return (
    <TextField
      error={error}
      variant={variant}
      size={size}
      label={label}
      helperText={helperText}
      FormHelperTextProps={{
        sx: {
          fontSize: '0.75rem',
          marginInline: 1.5,
        },
      }}
      {...field}
      {...textFieldProps}
      value={(field.value as string) ?? value}
      placeholder={PHONE_FIELD_PLACEHOLDER}
      InputProps={{
        // MUI doesn't support `disableUnderline` for `outlined` input variant ¯\_(ツ)_/¯
        ...((textFieldProps?.variant ?? variant) !== 'outlined'
          ? { disableUnderline: true }
          : {}),
        ...textFieldProps?.InputProps,
        inputComponent: TextMaskPhone,
        type: 'tel',
      }}
    />
  );
};

export const FormPhoneInput = (inProps: FormPhoneInputProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    label,
    value,
    required,
    pattern,
    minLength,
    maxLength,
    textFieldProps,
    variant: variantProp,
    size,
    showsIf = [],
    debounced,
    helperText,
    labelAbove = false,
    ...rest
  } = props;
  const variant = variantProp ?? (labelAbove ? 'outlined' : 'standard');
  const generatedInputId = useId();
  const inputId = textFieldProps?.id ?? generatedInputId;
  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return null;
  }

  return (
    <FormControl variant={variant} size={size} {...rest} error={hasErrorMsg}>
      {labelAbove ? (
        <FormFieldLabel htmlFor={inputId} disabled={!!textFieldProps?.disabled}>
          {label}
        </FormFieldLabel>
      ) : null}
      <Controller
        name={name}
        control={control}
        rules={{ required, pattern, minLength, maxLength }}
        render={({ field }) => (
          <RenderPhoneInput
            label={labelAbove ? undefined : label}
            field={field}
            textFieldProps={labelAbove ? { id: inputId, ...textFieldProps } : textFieldProps}
            variant={variant}
            size={size}
            error={hasErrorMsg}
            value={value}
            debounced={debounced}
            helperText={helperText}
          />
        )}
      />
    </FormControl>
  );
};
